import type { PostHogConfig } from "posthog-js";

import { maskSensitiveInput } from "./masking";
import { maskCapturedNetworkRequest, maskCredentialAttribute, sanitizeCapture } from "./sanitize";

/**
 * POSTHOG_DEFAULTS pins the SDK behaviour baseline so an upgrade never silently
 * changes what is captured. This date turns on history-driven pageviews (which is
 * what makes the web app's client-side router navigations register as page
 * views), and injects PostHog's own lazily-loaded scripts into <head> rather than
 * <body>, which keeps the documentation app's hydration root untouched.
 *
 * It matches the marketing website's baseline on purpose: the three properties
 * report into one project, so they must agree on what a pageview is.
 */
const POSTHOG_DEFAULTS = "2026-01-30" as const;

/**
 * AnalyticsInitOptions are the per-property knobs behind the shared capture
 * policy. apiHost is the ingest origin (the reverse proxy in every real
 * environment) and uiHost is the PostHog app itself, so links rendered by the
 * SDK resolve past the proxy. replay is the one capability that differs by
 * surface.
 */
export interface AnalyticsInitOptions {
  apiHost: string;
  replay: boolean;
  uiHost: string;
}

/**
 * buildInitOptions returns the posthog-js configuration shared by every Mountaya
 * property. Keeping it here rather than at each call site is the point of this
 * package: the capture and masking policy cannot drift between the application
 * and the documentation site.
 *
 * maskAllInputs is set explicitly, and that is load-bearing twice over. PostHog
 * skips the mask function entirely when the flag is off, so leaving it unset would
 * quietly turn the whole policy into a no-op. And the recorder resolves masking as
 * `client ?? server`, so an unset client value lets the project's own masking
 * setting decide; ours currently sends `maskAllInputs: false`, which would strip
 * the mask from every email and password field. Passing `true` from here is what
 * keeps that decision in the code rather than in a dashboard toggle.
 *
 * captureCanvas and strictMinimumDuration are here for the same reason, and both
 * are documented at their own keys below: every session_recording value this
 * package decides is stated outright, because the two ways it could be decided
 * elsewhere (a project toggle, or the `defaults` pin above) are both invisible
 * from the call site. The canvas frame rate and quality are the one deliberate
 * exception, left to the project so they can be tuned without a deploy; that is
 * documented at the key too, so an unset value there reads as a choice.
 *
 * No credential leaves through analytics. A page whose address carries one loads no SDK
 * (shouldEnableAnalytics), and for every other page `before_send` (sanitizeCapture) drops
 * what is raised on such a page reached by navigation, strips the credential parameters
 * from every address an event carries, its referrer included, and blanks them in every other
 * text, an autocaptured link's included, plain or percent-encoded. The replay's network mask
 * (maskCapturedNetworkRequest) does the same for the page addresses and the requests a
 * recording holds, and drops every request and response body (`recordBody: false`, and the
 * mask again behind it); its attribute mask (maskCredentialAttribute) blanks them in every
 * element attribute a recording holds, and its input mask (maskSensitiveInput) masks an input
 * whose value carries one. A credential is matched by what it is rather than by where it sits,
 * so the share sheet the studio package renders, whose markup this console does not own, is
 * covered with the rest. PostHog's own personal-data masking is left off on purpose: it also
 * masks the ad click ids the marketing site's attribution reads, and it does not reach the
 * referrer.
 */
export const buildInitOptions = ({
  apiHost,
  replay,
  uiHost,
}: AnalyticsInitOptions): Partial<PostHogConfig> => ({
  api_host: apiHost,
  before_send: sanitizeCapture,
  defaults: POSTHOG_DEFAULTS,
  disable_session_recording: !replay,
  session_recording: {
    // Canvas capture is on because the map is the product: without it the studio
    // is a blank box in every replay, which leaves the chrome and the clicks
    // legible and the thing the person was looking at missing. rrweb serializes
    // the DOM, and a WebGL canvas is opaque to that, so this flag is the only way
    // the map appears at all.
    //
    // It is half a decision. rrweb reads a canvas back out-of-band, which returns
    // the rendered frame only where the WebGL context was constructed with
    // preserveDrawingBuffer — and MapLibre reads that attribute once, at
    // construction. rrweb does try to force it on by patching getContext, but only
    // for contexts created after the recorder starts, and the recorder is two
    // dynamic imports and a flags round trip behind the app's first render, so the
    // map's context always predates the patch. Turning this on without the map
    // surfaces opting in yields transparent frames the recorder then discards.
    // isSessionReplayEnabled in the application is the other half.
    //
    // canvasFps and canvasQuality are deliberately left unset, unlike every other
    // key in this object. They are a volume knob rather than capture policy, and
    // replay weight is the open question here, so the project config should be
    // able to turn them down without a deploy. The recorder resolves each field as
    // `client ?? server ?? default` and falls back to 4 fps at quality 0.4.
    captureCanvas: { recordCanvas: true },
    maskAllInputs: true,
    // Every attribute the recorder writes, the first snapshot's and each later change's, goes
    // through this one function, which is what keeps a share token out of the `href` of the share
    // sheet's social links and the value of its link field. It has a price, paid knowingly: with
    // an attribute mask installed the recorder writes a changed `style` whole rather than as the
    // diff from its previous value, which makes a recording of moving DOM markers heavier.
    maskAttributeFn: maskCredentialAttribute,
    // The recorder runs both every captured network request and, as `{ name: href }`, every
    // page address it writes into a recording through this one function. Installing it takes
    // posthog's own body scrubber out of the chain, so the mask drops every body itself.
    maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
    maskInputFn: maskSensitiveInput,
    // Request and response bodies are never recorded: they carry the passwords, provider keys
    // and session tokens this console exchanges. The recorder resolves the flag as
    // `client !== false && project`, so only an explicit `false` here outvotes the project's
    // "capture network payloads" toggle; leaving it unset would hand the call to a dashboard.
    recordBody: false,
    // Part of what the `defaults` pin above asks for, restated because asking for
    // it there does not deliver it. PostHog merges the caller's config over the
    // resolved defaults one key deep, so this whole session_recording object
    // replaces the `{ strictMinimumDuration: true }` that `defaults` contributes
    // rather than merging into it. Without this line the pin is silently partial.
    strictMinimumDuration: true,
  },
  ui_host: uiHost,
});
